import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Candidate } from '../../models/job';
import { CandidateAssessment } from './CandidateAssessment';
import { CandidateAssessmentSummary } from '../../types/job.types';
import { CandidateQuestionsAnswers } from './CandidateQuestionsAnswers';
import { CandidateNotes } from './CandidateNotes';
import {
  extractCandidateData,
  formatExperience,
  formatExperienceDurationParts,
  getEnhancedDataValue,
  parseEnhancedData,
} from '../../hooks/useEnhancedData';
import { useCandidateSocialScoring, SocialPlatform } from '../../hooks/useCandidateSocialScoring';
import { Tag } from '../ui/atoms/Tag';
import { SkillChip } from '../ui/atoms/SkillChip';
import { Tooltip } from '../ui/tooltip';
import { Popover } from '@/components/ui/Popover';
import { ChevronDown, ExternalLink, Globe, Info, Sparkles } from 'lucide-react';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { IconWrapper } from '../ui/atoms/IconWrapper';
import { ProgressBar } from '../ui/atoms/ProgressBar';
import { Button } from '@/components/ui/atoms/Button';
import { getAssessmentContentLocale } from '@/utils/locale';
import type { StreamableVideo } from '../../hooks/useStreamableVideo';
import { RecordedVideo } from '../ui/molecules/RecordedVideo';

interface CandidateInfoProps {
  candidate: Candidate;
  hasAssessment: boolean;
  assessment: CandidateAssessmentSummary | null;
  isLoadingAssessmentSummary: boolean;
  /** Intro selfie video, streamed from the bucket or downloaded via the API. */
  selfieVideo: StreamableVideo;
  /** Propagates an admin's suspicious-activity flag removal up to the owner of
   *  the assessment state so every badge surface refreshes. */
  onAssessmentUpdated?: (assessment: CandidateAssessmentSummary) => void;
}

type SocialScoreModalKey = SocialPlatform;

type SocialAnalysisRow = {
  key: SocialScoreModalKey;
  label: string;
  logoSrc: string | null;
  url: string | null;
  blogUrls?: string[];
  score: string;
  isScored: boolean;
  isAnalyzing: boolean;
};

export const CandidateInfo: React.FC<CandidateInfoProps> = ({ 
  candidate,
  hasAssessment,
  assessment,
  isLoadingAssessmentSummary,
  selfieVideo,
  onAssessmentUpdated
}) => {
  const selfieVideoStatus = selfieVideo.status;
  const { t } = useTranslation(['job', 'candidates']);
  const [expandedSocialKey, setExpandedSocialKey] = useState<SocialScoreModalKey | null>(null);
  const [blogLinkPickerOpen, setBlogLinkPickerOpen] = useState(false);
  const blogExternalLinkRef = React.useRef<HTMLButtonElement | null>(null);

  const candidateData = extractCandidateData(candidate);
  const parsedEnhancedData = parseEnhancedData(candidate?.enhancedData);

  const { analyses, isAnalyzing, analyze } = useCandidateSocialScoring(candidate);
  const leetCodeAnalysisToDisplay = analyses.leetcode;
  const blogAnalysisToDisplay = analyses.blog;
  const googleScholarAnalysisToDisplay = analyses.google_scholar;
  const githubAnalysisToDisplay = analyses.github;

  useEffect(() => {
    setBlogLinkPickerOpen(false);
  }, [candidate.id]);

  const sectionDetailsAttrs = getGTMAttributes(createGTMTracking('META_CV_SECTION_VIEWED', { origin: 'meta_cv', target: 'candidate_details' }));
  const sectionAssessmentAttrs = getGTMAttributes(createGTMTracking('META_CV_SECTION_VIEWED', { origin: 'meta_cv', target: 'assessment' }));
  const sectionVideoAttrs = getGTMAttributes(createGTMTracking('META_CV_SECTION_VIEWED', { origin: 'meta_cv', target: 'video_selfie' }));
  const sectionQAAttrs = getGTMAttributes(createGTMTracking('META_CV_SECTION_VIEWED', { origin: 'meta_cv', target: 'questions_answers' }));
  const sectionNotesAttrs = getGTMAttributes(createGTMTracking('META_CV_SECTION_VIEWED', { origin: 'meta_cv', target: 'notes' }));
  const videoEngagedAttrs = getGTMAttributes(createGTMTracking('META_CV_VIDEO_ENGAGED', { origin: 'meta_cv' }));

  const normalizeUrl = (value?: string | null): string | null => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://${trimmed}`;
  };

  const linkedInUrl = normalizeUrl(candidateData.linkedin);
  const leetCodeUrl = normalizeUrl(candidateData.leetCode);
  const githubUrl = normalizeUrl(candidateData.github);
  const googleScholarUrl = normalizeUrl(candidateData.googleScholar);
  const kaggleUrl = normalizeUrl(candidateData.kaggle);
  const researchGateUrl = normalizeUrl(candidateData.researchGate);
  const blogUrl = normalizeUrl(candidateData.blog);

  const rawSocialLinks = useMemo((): unknown[] => {
    const sl = getEnhancedDataValue<unknown[]>(parsedEnhancedData, 'social_links');
    return Array.isArray(sl) ? sl : [];
  }, [parsedEnhancedData]);

  const blogUrls = useMemo(() => {
    const fromEnhanced = rawSocialLinks
      .filter(
        (e) =>
          typeof e === 'object' &&
          e !== null &&
          (e as Record<string, unknown>).type === 'blog' &&
          typeof (e as Record<string, unknown>).link === 'string'
      )
      .map((e) => normalizeUrl((e as Record<string, unknown>).link as string))
      .filter((u): u is string => Boolean(u));
    const merged = [...fromEnhanced, ...(blogUrl ? [blogUrl] : [])];
    return Array.from(new Set(merged));
  }, [rawSocialLinks, blogUrl]);

  const hasLeetCodeLink = Boolean(leetCodeUrl);
  const hasGithubLink = Boolean(githubUrl);
  const hasGoogleScholarLink = Boolean(googleScholarUrl);
  const hasBlogLinks = blogUrls.length > 0;

  const socialAnalysisRows = useMemo((): SocialAnalysisRow[] => {
    const rows: SocialAnalysisRow[] = [
      {
        key: 'leetcode',
        label: 'LeetCode',
        logoSrc: '/social-logos/leetcode.png',
        url: leetCodeUrl,
        score: typeof leetCodeAnalysisToDisplay?.scores?.S_total === 'number'
          ? `${leetCodeAnalysisToDisplay.scores.S_total}/100`
          : '--',
        isScored: Boolean(leetCodeAnalysisToDisplay),
        isAnalyzing: isAnalyzing.leetcode,
      },
      {
        key: 'github',
        label: 'GitHub',
        logoSrc: '/social-logos/github.png',
        url: githubUrl,
        score: typeof githubAnalysisToDisplay?.scores?.S_total === 'number'
          ? `${githubAnalysisToDisplay.scores.S_total}/100`
          : '--',
        isScored: Boolean(githubAnalysisToDisplay),
        isAnalyzing: isAnalyzing.github,
      },
      {
        key: 'google_scholar',
        label: 'Google Scholar',
        logoSrc: '/social-logos/google_scholar.png',
        url: googleScholarUrl,
        score: typeof googleScholarAnalysisToDisplay?.scores?.S_total === 'number'
          ? `${googleScholarAnalysisToDisplay.scores.S_total}/100`
          : '--',
        isScored: Boolean(googleScholarAnalysisToDisplay),
        isAnalyzing: isAnalyzing.google_scholar,
      },
    ];
    if (hasBlogLinks) {
      rows.push({
        key: 'blog',
        label: 'Blog',
        logoSrc: null,
        url: blogUrls[0] ?? null,
        blogUrls,
        score:
          typeof blogAnalysisToDisplay?.scores?.S_total === 'number'
            ? `${blogAnalysisToDisplay.scores.S_total}/100`
            : '--',
        isScored: Boolean(blogAnalysisToDisplay),
        isAnalyzing: isAnalyzing.blog,
      });
    }
    return rows.filter((row) => (row.key === 'blog' ? hasBlogLinks : Boolean(row.url)));
  }, [
    leetCodeUrl,
    leetCodeAnalysisToDisplay,
    isAnalyzing,
    githubUrl,
    githubAnalysisToDisplay,
    googleScholarUrl,
    googleScholarAnalysisToDisplay,
    hasBlogLinks,
    blogUrls,
    blogAnalysisToDisplay,
  ]);

  const otherOpenOnlyLinks = useMemo(() => ([
    {
      key: 'linkedin',
      label: 'LinkedIn',
      logoSrc: '/social-logos/linkedin.png',
      url: linkedInUrl,
    },
    {
      key: 'kaggle',
      label: 'Kaggle',
      logoSrc: '/social-logos/kaggle.png',
      url: kaggleUrl,
    },
    {
      key: 'researchgate',
      label: 'ResearchGate',
      logoSrc: '/social-logos/researchgate.png',
      url: researchGateUrl,
    },
  ]).filter((row) => Boolean(row.url)), [linkedInUrl, kaggleUrl, researchGateUrl]);

  const handleAnalyzePlatform = async (platform: SocialPlatform): Promise<void> => {
    const linkAvailable: Record<SocialPlatform, boolean> = {
      leetcode: hasLeetCodeLink,
      github: hasGithubLink,
      google_scholar: hasGoogleScholarLink,
      blog: hasBlogLinks,
    };
    const labels: Record<SocialPlatform, string> = {
      leetcode: 'LeetCode',
      github: 'GitHub',
      google_scholar: 'Google Scholar',
      blog: 'blog',
    };
    if (!linkAvailable[platform]) {
      toast.error(t('candidates:notifications.no_link_for_platform', { platform: labels[platform] }));
      return;
    }
    await analyze(platform);
  };

  const expandedSocialDetail = useMemo(() => {
    if (!expandedSocialKey) return null;

    if (expandedSocialKey === 'leetcode') {
      return {
        title: 'LeetCode Score Details',
        score: typeof leetCodeAnalysisToDisplay?.scores?.S_total === 'number'
          ? `${leetCodeAnalysisToDisplay.scores.S_total}/100`
          : '--',
        explanation: leetCodeAnalysisToDisplay?.summary
          || 'LeetCode scoring uses weighted difficulty solving, consistency, and algorithmic coverage depth.',
        subScores: [
          {
            key: 'problems',
            label: 'Difficulty-Weighted Problem Solving',
            score: typeof leetCodeAnalysisToDisplay?.scores?.S_problems === 'number'
              ? `${Math.round(leetCodeAnalysisToDisplay.scores.S_problems)}/40`
              : 'N/A',
            helpText:
              'Overall problem-solving strength, with emphasis on complex challenges over simple volume.',
          },
          {
            key: 'consistency',
            label: 'Practice Consistency (Streak + Active Days)',
            score: typeof leetCodeAnalysisToDisplay?.scores?.S_cons === 'number'
              ? `${Math.round(leetCodeAnalysisToDisplay.scores.S_cons)}/22`
              : 'N/A',
            helpText:
              'Practice regularity and discipline over time, based on sustained activity and streak patterns.',
          },
          {
            key: 'algo',
            label: 'Algorithmic Topic Coverage (Fundamental to Advanced)',
            score: typeof leetCodeAnalysisToDisplay?.scores?.S_algo === 'number'
              ? `${Math.round(leetCodeAnalysisToDisplay.scores.S_algo)}/38`
              : 'N/A',
            helpText:
              'Breadth and depth across algorithmic topics, covering fundamentals through advanced concepts.',
          },
        ],
      };
    }

    if (expandedSocialKey === 'github') {
      const gh = githubAnalysisToDisplay?.scores;
      return {
        title: 'GitHub Score Details',
        score:
          typeof gh?.S_total === 'number'
            ? `${gh.S_total}/100`
            : hasGithubLink
              ? '--'
              : 'No link',
        explanation:
          githubAnalysisToDisplay?.summary ||
          (hasGithubLink
            ? 'GitHub scoring combines yearly contribution volume, project impact (stars + forks + followers), language breadth, and a qualitative depth/authenticity signal extracted from READMEs and commits — same async pipeline (task + stored analysis) as LeetCode, Scholar, and blog scoring.'
            : 'No GitHub profile link found for this candidate.'),
        subScores: [
          {
            key: 'volume',
            label: 'Contribution Volume (last year + owned repos)',
            score: typeof gh?.S_volume === 'number' ? `${Math.round(gh.S_volume)}/25` : 'N/A',
            helpText:
              'Yearly contribution count and original repository count, log-normalised so prolific accounts saturate gracefully.',
          },
          {
            key: 'impact',
            label: 'Project Impact (stars / forks / followers)',
            score: typeof gh?.S_impact === 'number' ? `${Math.round(gh.S_impact)}/30` : 'N/A',
            helpText:
              'Reach signal across owned non-fork repos: total stargazers, forks, and follower count combined into one impact axis.',
          },
          {
            key: 'breadth',
            label: 'Language Breadth',
            score: typeof gh?.S_breadth === 'number' ? `${Math.round(gh.S_breadth)}/20` : 'N/A',
            helpText:
              'Distinct primary languages across owned repos. Tier table: 1→4, 2→8, 3-4→12, 5-7→16, 8+→20.',
          },
          {
            key: 'signal',
            label: 'Qualitative Signal (depth + authenticity)',
            score: typeof gh?.S_signal === 'number' ? `${Math.round(gh.S_signal)}/25` : 'N/A',
            helpText:
              typeof gh?.primary_persona === 'string' && typeof gh?.project_archetype === 'string'
                ? `LLM analysis of READMEs + commits: persona ${gh.primary_persona}, archetype ${gh.project_archetype}, AI-likelihood ${gh.ai_generated_likelihood ?? 'n/a'}.`
                : 'LLM analysis of READMEs and commits — technical depth (1-5) blended with authenticity (human-authored vs AI-generated indicators).',
          },
        ],
      };
    }

    if (expandedSocialKey === 'google_scholar') {
      const gs = googleScholarAnalysisToDisplay?.scores;
      return {
        title: 'Google Scholar Score Details',
        score:
          typeof gs?.S_total === 'number'
            ? `${gs.S_total}/100`
            : hasGoogleScholarLink
              ? '--'
              : 'No link',
        explanation:
          googleScholarAnalysisToDisplay?.summary ||
          (hasGoogleScholarLink
            ? 'Google Scholar scoring combines publication volume, citation impact (citations + h-index), recent activity (last 5 years), and estimated first-author share — same async pipeline (task + stored analysis) as LeetCode and blog scoring.'
            : 'No Google Scholar link found for this candidate.'),
        subScores: [
          {
            key: 'volume',
            label: 'Publication Volume',
            score: typeof gs?.S_volume === 'number' ? `${Math.round(gs.S_volume)}/25` : 'N/A',
            helpText:
              'Log-normalized total publication count, capped so a few high-impact papers do not dominate.',
          },
          {
            key: 'impact',
            label: 'Research Impact (Citations + h-index)',
            score: typeof gs?.S_impact === 'number' ? `${Math.round(gs.S_impact)}/40` : 'N/A',
            helpText:
              'Weighted blend of total citations, h-index, and average citations per publication.',
          },
          {
            key: 'recent',
            label: 'Recent Activity (Last 5 Years)',
            score: typeof gs?.S_recent === 'number' ? `${Math.round(gs.S_recent)}/20` : 'N/A',
            helpText:
              'Share of recent publications and citations relative to the full profile, plus per-paper recent velocity.',
          },
          {
            key: 'role',
            label: 'Role Significance (First-author share)',
            score: typeof gs?.S_role === 'number' ? `${Math.round(gs.S_role)}/15` : 'N/A',
            helpText:
              'Estimated proportion of first-author publications based on surname matching against the leading author.',
          },
        ],
      };
    }

    if (expandedSocialKey === 'blog') {
      const bs = blogAnalysisToDisplay?.scores;
      return {
        title: 'Blog & portfolio score details',
        score:
          typeof bs?.S_total === 'number'
            ? `${bs.S_total}/100`
            : hasBlogLinks
              ? '--'
              : 'No link',
        explanation:
          blogAnalysisToDisplay?.summary ||
          (hasBlogLinks
            ? 'Blog scoring samples syndicated posts, runs structured LLM feature extraction (depth, thought leadership, persona, engagement), optionally matches against the CV excerpt, then composes a recruiter summary—same pipeline style as LeetCode (async task + stored analysis).'
            : 'No blog or portfolio links found for this candidate.'),
        subScores: [
          {
            key: 'tech_depth',
            label: 'Technical depth',
            score: typeof bs?.tech_depth === 'number' ? `${Math.round(bs.tech_depth)}/5` : 'N/A',
            helpText:
              '1 = surface concepts, 5 = expert deep-dives. Taken from GPT structured parse of sampled post excerpts.',
          },
          {
            key: 'thought',
            label: 'Thought leadership',
            score:
              typeof bs?.thought_leadership_score === 'number'
                ? `${bs.thought_leadership_score.toFixed(2)}/1`
                : 'N/A',
            helpText: 'Originality vs regurgitation (0–1) from the same structured extraction.',
          },
          {
            key: 'persona',
            label: 'Persona & tone',
            score:
              typeof bs?.primary_persona === 'string' && typeof bs?.sentiment_archetype === 'string'
                ? `${bs.primary_persona} · ${bs.sentiment_archetype}`
                : 'N/A',
            helpText: 'Architect / Learner / Evangelist and Collaborative / Critical / Constructive.',
          },
          {
            key: 'posting',
            label: 'Posting signal',
            score:
              typeof bs?.posting_total_articles_found === 'number'
                ? `~${bs.posting_total_articles_found} posts (est.)`
                : 'N/A',
            helpText:
              'Model-estimated volume/recency from excerpts; feed also reports entry_count and snippets_used in scores.',
          },
          {
            key: 'identity',
            label: 'CV ↔ blog match (optional)',
            score:
              typeof bs?.identity_confidence === 'number'
                ? `${bs.identity_confidence.toFixed(2)} confidence`
                : 'N/A',
            helpText:
              'When a CV excerpt exists, a second structured call scores whether the blog likely belongs to the same person.',
          },
        ],
      };
    }
  }, [
    expandedSocialKey,
    leetCodeAnalysisToDisplay,
    githubAnalysisToDisplay,
    blogAnalysisToDisplay,
    googleScholarAnalysisToDisplay,
    hasGithubLink,
    hasGoogleScholarLink,
    hasBlogLinks,
  ]);

  const getSocialScoreTier = (score: number): { label: string; intent: 'primary' | 'info' | 'secondary' | 'error' } => {
    if (score >= 90) return { label: 'Elite', intent: 'primary' };
    if (score >= 75) return { label: 'Strong', intent: 'info' };
    if (score >= 55) return { label: 'Solid', intent: 'secondary'};
    if (score >= 35) return { label: 'Novice', intent: 'secondary' };
    return { label: 'Rookie', intent: 'error' };
  };

  const getSocialTierByKey = (key: SocialScoreModalKey): { label: string; intent: 'primary' | 'info' | 'secondary' | 'error' } | null => {
    if (key === 'leetcode' && typeof leetCodeAnalysisToDisplay?.scores?.S_total === 'number') {
      return getSocialScoreTier(leetCodeAnalysisToDisplay.scores.S_total);
    }
    if (key === 'blog' && typeof blogAnalysisToDisplay?.scores?.S_total === 'number') {
      return getSocialScoreTier(blogAnalysisToDisplay.scores.S_total);
    }
    if (key === 'google_scholar' && typeof googleScholarAnalysisToDisplay?.scores?.S_total === 'number') {
      return getSocialScoreTier(googleScholarAnalysisToDisplay.scores.S_total);
    }
    if (key === 'github' && typeof githubAnalysisToDisplay?.scores?.S_total === 'number') {
      return getSocialScoreTier(githubAnalysisToDisplay.scores.S_total);
    }
    return null;
  };

  const getNumericSocialScore = (key: SocialScoreModalKey): number | null => {
    const sources: Record<SocialScoreModalKey, unknown> = {
      leetcode: leetCodeAnalysisToDisplay?.scores?.S_total,
      github: githubAnalysisToDisplay?.scores?.S_total,
      google_scholar: googleScholarAnalysisToDisplay?.scores?.S_total,
      blog: blogAnalysisToDisplay?.scores?.S_total,
    };
    const value = sources[key];
    return typeof value === 'number' ? value : null;
  };

  return (
    <div className="space-y-6">
      {/* Section: Candidate Detail */}
      <div className="space-y-6" {...sectionDetailsAttrs}>
        <h5 className="border-b border-gray-200 pb-2">{t('candidate_info.title')}</h5>

      {/* Grid layout for detailed sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Skill Stack / Technical Skills */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="mb-3">{t('candidate_info.skill_stack')}</h6>
          {candidateData.technicalSkills && candidateData.technicalSkills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {candidateData.technicalSkills.map((skill, index) => (
                <SkillChip key={index} label={skill} tone="info" />
              ))}
            </div>
          ) : (
            <p className="text-dark-invirtus/80 text-sm font-body">{t('candidate_info.no_skills')}</p>
          )}
        </div>

        {/* Experience */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="mb-3">{t('candidate_info.experience')}</h6>
          {candidateData.experience && candidateData.experience.length > 0 ? (
            <ul className="space-y-2">
              {candidateData.experience.map((exp, idx) => (
                <li key={idx} className="text-dark-invirtus/80 text-pretty flex items-start font-body text-sm">
                  <span className="mr-2">•</span>
                  <span>{formatExperience(exp, candidateData.sourceLocale)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-dark-invirtus/80 text-sm font-body">{t('candidate_info.no_experience')}</p>
          )}
          {candidateData.experienceSummary && (
          <div className="flex items-center gap-2 mt-3 text-dark-invirtus">
            <h6 className="text-base">{t('candidate_info.total_experience')}</h6>
            <Tag intent="primary" variant="subtle" size="lg"> 
              {formatExperienceDurationParts(
                candidateData.experienceSummary.years,
                candidateData.experienceSummary.months,
                candidateData.sourceLocale,
              )}                      
            </Tag>
            
          </div>
          )}
        </div>

        {/* Languages */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="mb-3">{t('candidate_info.languages')}</h6>
          {candidateData.languages && candidateData.languages.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {candidateData.languages.map((language, index) => (
                <Tag intent="secondary" variant="subtle" key={index}>
                  {language}                      
                </Tag>
              ))}
            </div>
          ) : (
            <p className="text-dark-invirtus/80 text-sm font-body">{t('candidate_info.no_languages')}</p>
          )}
        </div>

        {/* Education */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="mb-3">{t('candidate_info.education')}</h6>
          {candidateData.education && candidateData.education.length > 0 ? (
            <ul className="space-y-2">
              {candidateData.education.map((edu, idx) => (
                <li key={idx} className="text-dark-invirtus/80 flex items-start font-body text-sm">
                  <span className="mr-2">•</span>
                  <span>{edu}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-dark-invirtus/80 text-sm font-body">{t('candidate_info.no_education')}</p>
          )}
        </div>

        {/* Social Links */}
        {(socialAnalysisRows.length > 0 || otherOpenOnlyLinks.length > 0) && (
        <div className="w-full max-w-full justify-self-start bg-white p-4 rounded-lg border border-gray-200 md:col-span-2">
          <h6 className="mb-3">{t('candidate_info.social_links')}</h6>
          <div className={`grid grid-cols-1 gap-4 ${
            socialAnalysisRows.length > 0 && otherOpenOnlyLinks.length > 0 ? 'md:grid-cols-2' : ''
          }`}>
            {socialAnalysisRows.length > 0 && (
            <div className="space-y-2">
              {
                socialAnalysisRows.map((socialRow) => {
                  const socialTier = getSocialTierByKey(socialRow.key);
                  const isExpanded = expandedSocialKey === socialRow.key;
                  const numericScore = getNumericSocialScore(socialRow.key);
                  const detail = isExpanded ? expandedSocialDetail : null;
                  const hasBlogPicker =
                    socialRow.key === 'blog' && (socialRow.blogUrls?.length ?? 0) > 1;
                  const canOpenLink =
                    socialRow.key === 'blog'
                      ? (socialRow.blogUrls?.length ?? 0) === 1
                      : Boolean(socialRow.url);

                  return (
                    <div
                      key={socialRow.key}
                      className="rounded-lg border border-gray-200 bg-white overflow-hidden transition-colors"
                    >
                      <div
                        className="grid items-center gap-2 p-2.5 min-h-[56px] grid-cols-[36px_minmax(0,1fr)_auto_32px] sm:grid-cols-[36px_minmax(0,1fr)_112px_84px_32px]"
                      >
                        {/* Col 1 — Logo */}
                        <div className="flex items-center justify-center">
                          {socialRow.logoSrc ? (
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-50">
                              <img
                                src={socialRow.logoSrc}
                                alt={`${socialRow.label} logo`}
                                className="h-6 w-6 object-contain"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <IconWrapper size="md" color="secondary">
                              <Globe />
                            </IconWrapper>
                          )}
                        </div>

                        {/* Col 2 — Name + tier badge (badge right-aligned so it lines up across rows) */}
                        <div className="min-w-0 flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold font-body text-dark-invirtus truncate">
                            {socialRow.label}
                          </p>
                          {socialTier && (
                            <Tag
                              intent={socialTier.intent}
                              variant="subtle"
                              className="shrink-0 w-[60px] justify-center"
                            >
                              {socialTier.label}
                            </Tag>
                          )}
                        </div>

                        {/* Col 3 — Score visualization (desktop only). Always rendered so columns align. */}
                        <div className="hidden sm:flex items-center gap-1.5">
                          {socialRow.isScored && numericScore !== null ? (
                            <ProgressBar
                              value={Math.max(0, Math.min(100, numericScore))}
                              max={100}
                              size="sm"
                              intent="primary"
                              className="flex-1 min-w-0"
                              aria-label={`${socialRow.label} score`}
                            />
                          ) : (
                            <div className="h-1.5 flex-1 min-w-0 rounded-full bg-[var(--color-dark-invirtus-soft)]" />
                          )}
                          <span
                            className={`shrink-0 text-[11px] font-semibold tabular-nums whitespace-nowrap ${
                              socialRow.isScored ? 'text-dark-invirtus' : 'text-dark-invirtus/35'
                            }`}
                          >
                            {socialRow.isScored && numericScore !== null
                              ? Math.round(numericScore)
                              : '—'}
                            <span className={socialRow.isScored ? 'text-dark-invirtus/50' : ''}>/100</span>
                          </span>
                        </div>

                        {/* Col 4 — Action chip (Score / Details) */}
                        <div className="flex items-center justify-end">
                          {socialRow.isScored ? (
                            <Button
                              type="contained"
                              intent={isExpanded ? 'primary' : 'muted'}
                              size="xs"
                              label="Details"
                              rightIcon={<ChevronDown className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />}
                              onClick={() =>
                                setExpandedSocialKey(isExpanded ? null : socialRow.key)
                              }
                              aria-expanded={isExpanded}
                              aria-label={`${isExpanded ? 'Hide' : 'Show'} ${socialRow.label} score breakdown`}
                            />
                          ) : (
                            <Button
                              type="contained"
                              intent="primary"
                              size="xs"
                              label={socialRow.isAnalyzing ? 'Scoring' : 'Score'}
                              icon={<Sparkles/>}
                              loading={socialRow.isAnalyzing}
                              onClick={() => {
                                void handleAnalyzePlatform(socialRow.key);
                              }}
                              disabled={
                                socialRow.isAnalyzing ||
                                (socialRow.key === 'blog'
                                  ? (socialRow.blogUrls?.length ?? 0) === 0
                                  : !socialRow.url)
                              }
                              className="w-auto sm:w-[84px]"
                              aria-label={`Score ${socialRow.label} profile`}
                            />
                          )}
                        </div>

                        {/* Col 5 — External link / blog picker trigger */}
                        <div className="flex items-center justify-center">
                          {hasBlogPicker ? (
                            <>
                              <Tooltip content={t('candidates:tooltips.choose_blog_link')}>
                                <Button
                                  type="icon"
                                  intent="muted"
                                  size="sm"
                                  ref={blogExternalLinkRef}
                                  onClick={() => setBlogLinkPickerOpen((open) => !open)}
                                  aria-label="Choose blog link to open"
                                  label="Choose blog link to open"
                                  icon={<ExternalLink />}
                                />
                              </Tooltip>
                              <Popover
                                open={blogLinkPickerOpen}
                                onClose={() => setBlogLinkPickerOpen(false)}
                                anchorRef={blogExternalLinkRef}
                                className="bottom-full right-0 mb-2"
                                contentClassName="min-w-[240px] max-w-sm p-2 bg-white border border-gray-200 rounded-lg shadow-lg"
                              >
                                <p className="px-1 pb-2 text-xs font-semibold text-dark-invirtus/80">
                                  {t('candidates:tooltips.open_blog')}
                                </p>
                                <ul className="max-h-48 space-y-1 overflow-y-auto">
                                  {socialRow.blogUrls?.map((blogLinkUrl) => (
                                    <li key={blogLinkUrl}>
                                      <button
                                        type="button"
                                        className="w-full rounded px-2 py-1.5 text-left text-xs text-dark-invirtus hover:bg-gray-50 truncate"
                                        onClick={() => {
                                          window.open(blogLinkUrl, '_blank', 'noopener,noreferrer');
                                          setBlogLinkPickerOpen(false);
                                        }}
                                      >
                                        {blogLinkUrl}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </Popover>
                            </>
                          ) : (
                            <Tooltip content={canOpenLink ? t('candidates:tooltips.open_profile') : t('candidates:tooltips.no_link')}>
                              <Button
                                type="icon"
                                intent="muted"
                                size="sm"
                                onClick={() => {
                                  if (
                                    socialRow.key === 'blog' &&
                                    (socialRow.blogUrls?.length ?? 0) === 1
                                  ) {
                                    const only = socialRow.blogUrls?.[0];
                                    if (only)
                                      window.open(only, '_blank', 'noopener,noreferrer');
                                    return;
                                  }
                                  if (socialRow.url) {
                                    window.open(socialRow.url, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                disabled={!canOpenLink}
                                aria-label={`Open ${socialRow.label} profile`}
                                label={`Open ${socialRow.label} profile`}
                                icon={<ExternalLink />}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {isExpanded && detail && (
                          <motion.div
                            key="detail"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden border-t border-gray-100 bg-[var(--color-background-gray)]"
                          >
                            <div className="p-3 space-y-3">
                              {socialRow.isScored && numericScore !== null && (
                                <div className="sm:hidden flex items-center gap-2">
                                  <ProgressBar
                                    value={Math.max(0, Math.min(100, numericScore))}
                                    max={100}
                                    size="sm"
                                    intent="primary"
                                    className="flex-1"
                                    aria-label={`${socialRow.label} score`}
                                  />
                                  <span className="text-xs font-semibold tabular-nums text-dark-invirtus">
                                    {Math.round(numericScore)}/100
                                  </span>
                                </div>
                              )}
                              <p className="text-xs leading-relaxed text-dark-invirtus/80 font-body">
                                {detail.explanation}
                              </p>
                              {detail.subScores && detail.subScores.length > 0 && (
                                <div className="grid grid-cols-1 gap-1.5">
                                  {detail.subScores.map((subScore) => {
                                    const isNumericScore = /^\d+(\.\d+)?\/\d+$/.test(subScore.score);
                                    return (
                                    <div
                                      key={subScore.key}
                                      className="flex items-center justify-between gap-2 rounded-md bg-white border border-gray-200 px-2.5 py-2"
                                    >
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <Tooltip
                                          content={subScore.helpText}
                                          className="whitespace-normal max-w-xs text-left leading-5"
                                        >
                                          <Button
                                            type="icon"
                                            intent="muted"
                                            size="xs"
                                            aria-label={`Explain ${subScore.label}`}
                                            label={`Explain ${subScore.label}`}
                                            icon={<Info />}
                                          />
                                        </Tooltip>
                                        <p className="text-xs text-dark-invirtus/90 font-body truncate">
                                          {subScore.label}
                                        </p>
                                      </div>
                                      {isNumericScore ? (
                                        <Tag
                                          intent="secondary"
                                          variant="subtle"
                                          className="shrink-0 w-[68px] justify-center  tabular-nums"
                                        >
                                          {subScore.score}
                                        </Tag>
                                      ) : (
                                        <Tag
                                          intent="secondary"
                                          variant="subtle"
                                          className="shrink-0 max-w-[200px] justify-center  whitespace-nowrap normal-case"
                                        >
                                          {subScore.score}
                                        </Tag>
                                      )}
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })
              }
            </div>
            )}

            {otherOpenOnlyLinks.length > 0 && (
            <div className="space-y-2">
              {
                otherOpenOnlyLinks.map((otherLink) => (
                  <div
                    key={otherLink.key}
                    className="grid items-center gap-2 p-2.5 min-h-[56px] grid-cols-[36px_minmax(0,1fr)_32px] rounded-lg border border-gray-200 bg-white"
                  >
                    <div className="flex items-center justify-center">
                      {otherLink.logoSrc ? (
                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-50">
                          <img
                            src={otherLink.logoSrc}
                            alt={`${otherLink.label} logo`}
                            className="h-6 w-6 object-contain"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <IconWrapper size="md" color="secondary">
                          <Globe />
                        </IconWrapper>
                      )}
                    </div>
                    <p className="text-sm font-semibold font-body text-dark-invirtus truncate">
                      {otherLink.label}
                    </p>
                    <div className="flex items-center justify-center">
                      <Tooltip content={otherLink.url ? t('candidates:tooltips.open_profile') : t('candidates:tooltips.no_link')}>
                        <Button
                          type="icon"
                          intent="muted"
                          size="sm"
                          onClick={() => {
                            if (otherLink.url) {
                              window.open(otherLink.url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          disabled={!otherLink.url}
                          aria-label={`Open ${otherLink.label} profile`}
                          label={`Open ${otherLink.label} profile`}
                          icon={<ExternalLink />}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ))
              }
            </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* CV/Job Match and Mismatch - Two Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CV/Job Match */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="mb-3 ">{t('candidates:meta_cv.cv_match.title')}</h6>
          {candidateData.strengths && candidateData.strengths.length > 0 ? (
            <ul className="space-y-2">
              {candidateData.strengths.map((strength, idx) => (
                <li key={idx} className="flex items-start text-dark-invirtus/80 font-body text-sm">
                  <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-dark-invirtus/80 text-sm font-body">{t('candidates:meta_cv.cv_match.empty')}</p>
          )}
        </div>
        
        {/* CV/Job Mismatch */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="mb-3">{t('candidates:meta_cv.cv_mismatch.title')}</h6>
          {candidateData.weaknesses && candidateData.weaknesses.length > 0 ? (
            <ul className="space-y-2">
              {candidateData.weaknesses.map((weakness, idx) => (
                <li key={idx} className="flex items-start text-dark-invirtus/80 font-body text-sm">
                  <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>{weakness}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-dark-invirtus/80 text-sm font-body">{t('candidates:meta_cv.cv_mismatch.empty')}</p>
          )}
        </div>
      </div>
      </div>

      {/* Assessment Section - Only show when hasAssessment is true */}
      {hasAssessment && (
        <div className="space-y-6" {...sectionAssessmentAttrs}>
          <CandidateAssessment 
            assessment={assessment}
            isLoadingAssessmentSummary={isLoadingAssessmentSummary}
          />
          
          {/* Selfie Video Section */}
          {selfieVideoStatus !== 'none' && (
            <div className='flex flex-col items-center justify-center' {...sectionVideoAttrs}>
              <h5 className="w-full border-b border-gray-200 pb-2">{t('candidates:meta_cv.selfie.title')}</h5>
              <div className="w-full max-w-3xl my-6" {...videoEngagedAttrs}>
                <RecordedVideo
                  video={selfieVideo}
                  loadingLabel={t('candidates:meta_cv.selfie.loading')}
                  unavailableLabel={t('candidates:meta_cv.selfie.error')}
                  className="w-full aspect-video shadow-md"
                />
              </div>
            </div>
          )}
          {selfieVideoStatus === 'none' && (
             <p className="text-gray-700 italic font-body text-sm">{t('candidates:meta_cv.selfie.none')}</p>
          )}
        </div>
      )}

      {/* Questions and Answers Section */}
      <div {...sectionQAAttrs}>
        <CandidateQuestionsAnswers
          candidateId={candidate.id}
          hasAssessment={hasAssessment}
          cheatingDetection={assessment?.cheating_detection}
          perQuestionScores={assessment?.per_question_scores}
          assessmentContentLocale={getAssessmentContentLocale(assessment)}
          onCheatingFlagCleared={onAssessmentUpdated}
        />
      </div>

      {/* Talent-level Notes — always at the bottom (after Q&A when interviewed,
          directly after the CV match otherwise). Synced with the Talent page. */}
      <div {...sectionNotesAttrs}>
        <CandidateNotes talentPoolProfileId={candidate.talentPoolProfileId} />
      </div>

    </div>
  );
};
